import {action, comparer, makeObservable, observable, reaction} from "mobx";
import {
	annotation,
	annotationUuid,
	annotationWithPosition,
	mergedOutline,
	mergedOutline2,
	mergedOutlineTopic,
	mergedOutlineTopic2,
	outline,
	outlineItem,
	outlineTopic,
	outlineTopicType,
	projectUuid,
	syncData,
	syncHandler,
	topicUuid
} from "../model/index.js";
import app from "../app.js";
import {loadObject} from "../utils/index.js";
import annotationStore from "./annotation.js";
import topicStore from "./topic.js";
import {outSyncHandler, Sync} from "../sync/index.js";
import {Store} from "./index.js";
import {fetchLockOptions} from "./common.js";
import AsyncLock from "async-lock";

export class OutlineStore implements Store {
	_outlines: Record<projectUuid, outline> = {};
	_fetchLock = new AsyncLock(fetchLockOptions);

	constructor() {
		makeObservable(this, {
			_clear:                          action,
			_outlines:                       observable,
			_load:                           action,
			_removeAnnotation:               action,
			_removeTopic:                    action,
			_updateFromSync:                 action,
			_loadAnnotationIntoTopic:        action,
			_loadAnnotationIntoSpecialTopic: action,
			_remove:                         action
		});
	}

	_clear() {
		this._outlines = {};
	}

	_refresh() {
		(Object.keys(this._outlines) as projectUuid[])
			.forEach(projectUuid => this._fetch(projectUuid, true));
	}

	_isProjectLoaded(projectUuid: projectUuid): boolean {
		return this._outlines[projectUuid] !== undefined;
	}

	/**
	 * Returns a merged outline, ensuring all topics are present and in the correct order.
	 * @deprecated
	 */
	byProject(projectUuid: projectUuid): mergedOutline {
		if (!this._isProjectLoaded(projectUuid)) // noinspection JSIgnoredPromiseFromCall
			this._fetch(projectUuid);
		if (!annotationStore._isProjectLoaded(projectUuid)) // noinspection JSIgnoredPromiseFromCall
			annotationStore._fetchByProject(projectUuid);
		if (!topicStore._isProjectLoaded(projectUuid)) // noinspection JSIgnoredPromiseFromCall
			topicStore._fetchByProject(projectUuid);

		const outline = this._outlines[projectUuid];
		if (outline === undefined)
			return [];

		/**
		 * For a given outline item, find the full annotation and return it with its position
		 * (or `null` if we can't find it).
		 */
		function getAnnotationWithPosition(item: outlineItem): annotationWithPosition | null {
			if (!annotationStore._isProjectLoaded(projectUuid))
				return null;

			const annotation = annotationStore._annotations[projectUuid][item.annotation_uuid];

			return annotation
				? Object.assign({position: item.position}, annotation)
				: null;
		}

		/**
		 * Return a shallow copy of the outline topic, with full annotation objects instead of just the UUIDs.
		 */
		function mergeOutlineTopic(outlineTopic: outlineTopic): mergedOutlineTopic {
			return Object.assign({}, outlineTopic, {
				annotations: outlineTopic.annotations
					.map(getAnnotationWithPosition)
					.filter(annotation => annotation !== null)
			});
		}

		/**
		 * Removes missing annotations and topics from the given outline.
		 * The outline should have the full annotation objects (i.e. not just the {annotation_uuid, position} object).
		 */
		function cleanUp(outline: mergedOutline): mergedOutline {
			return outline.filter(ot => ot.annotations && ot.annotations.length > 0);
		}

		/**
		 * Adds topics from `this.topics` to the outline if they're not already present.
		 * Does not guarantee a sorted output.
		 */
		function ensureHasAllTopics(outline: mergedOutline): void {
			topicStore.byProject(projectUuid)
				.filter(t => !outline.find(ot => "topic_uuid" in ot && ot.topic_uuid === t.uuid))
				.forEach(t => outline.push({topic_uuid: t.uuid, annotations: []}));
		}

		/**
		 * Sorts the outline according to the position values from the topics.
		 * Takes into account "special topics" such as "bookmarks".
		 */
		function sortTopics(outline: mergedOutline): void {
			/**
			 * This function gets the position for the given outline topic. Returns 0 if it can't find it.
			 *
			 * @param {Object} outline_topic Expects it to have either "topic_uuid" or "type" props.
			 * @return {number}
			 */
			const pos = (outline_topic: mergedOutlineTopic) => {
				let position = 0;
				if ("topic_uuid" in outline_topic) {
					const topic = topicStore._topics[outline_topic.topic_uuid];
					if (topic)
						position = topic.position;
				} else {
					switch (outline_topic.type) {
						case "bookmarks":
							position = Number.MIN_SAFE_INTEGER; // Show bookmarks first
							break;
					}
				}
				return position;
			};

			outline.sort((a, b) => pos(a) - pos(b));
		}

		/**
		 * Sorts the annotations for each topic in the outline (each annotation has a "position" value).
		 */
		function sortAnnotations(outline: mergedOutline): void {
			outline
				.filter(ot => ot.annotations) // Skip if topic has no annotations
				.forEach(ot => ot.annotations.sort((a, b) => a.position - b.position));
		}

		const mergedOutline = cleanUp(outline.map(mergeOutlineTopic));
		ensureHasAllTopics(mergedOutline);
		sortTopics(mergedOutline);
		sortAnnotations(mergedOutline);
		return mergedOutline;
	}

	/**
	 * Returns a merged outline, ensuring all topics are present and in the correct order.
	 */
	getMergedByProject(projectUuid: projectUuid): mergedOutline2 {
		if (!this._isProjectLoaded(projectUuid)) // noinspection JSIgnoredPromiseFromCall
			this._fetch(projectUuid);
		if (!annotationStore._isProjectLoaded(projectUuid)) // noinspection JSIgnoredPromiseFromCall
			annotationStore._fetchByProject(projectUuid);
		if (!topicStore._isProjectLoaded(projectUuid)) // noinspection JSIgnoredPromiseFromCall
			topicStore._fetchByProject(projectUuid);

		let outline = this._outlines[projectUuid] ?? undefined;
		if (outline === undefined)
			return [];

		/**
		 * Sorts the annotations for each topic in the outline (each annotation has a "position" value).
		 */
		function sortAnnotations(outline: outlineTopic[]): outlineTopic[] {
			return outline
				.filter(ot => ot.annotations) // Skip if topic has no annotations
				.map(ot => {
					const {annotations} = ot;
					const sorted = [...annotations]; // don't mutate original
					sorted.sort((a, b) => a.position - b.position);
					return Object.assign({}, ot, {annotations: sorted});
				});
		}

		/**
		 * For a given outline item, find the full annotation and return it with its position
		 * (or `null` if we can't find it).
		 */
		function getAnnotation(item: outlineItem): annotation | null {
			if (!annotationStore._isProjectLoaded(projectUuid))
				return null;

			return annotationStore._annotations[projectUuid][item.annotation_uuid];
		}

		/**
		 * Return a shallow copy of the outline topic, with full annotation objects instead of just the UUIDs.
		 */
		function mergeOutlineTopic(outlineTopic: outlineTopic): mergedOutlineTopic2 {
			const additional = {
				annotations: outlineTopic.annotations
					.map(getAnnotation)
					.filter(annotation => annotation !== null)
			};
			// @ts-ignore
			if (outlineTopic.topic_uuid)
				// @ts-ignore
				additional.topic = topicStore.byUuid(outlineTopic.topic_uuid);
			return Object.assign({}, outlineTopic, additional) as mergedOutlineTopic2;
		}

		/**
		 * Removes missing annotations and topics from the given outline.
		 * The outline should have the full annotation objects (i.e. not just the {annotation_uuid, position} object).
		 */
		function cleanUp(outline: mergedOutline2): mergedOutline2 {
			return outline.filter(ot => ot.annotations && ot.annotations.length > 0);
		}

		/**
		 * Adds topics from `this.topics` to the outline if they're not already present.
		 * Does not guarantee a sorted output.
		 */
		function ensureHasAllTopics(outline: mergedOutline2): void {
			topicStore.byProject(projectUuid)
				.filter(t => !outline.find(ot => "topic_uuid" in ot && ot.topic_uuid === t.uuid))
				.forEach(t => outline.push({topic_uuid: t.uuid, topic: t, annotations: []}));
		}

		/**
		 * Sorts the outline according to the position values from the topics.
		 * Takes into account "special topics" such as "bookmarks".
		 */
		function sortTopics(outline: mergedOutline2): void {
			/**
			 * This function gets the position for the given outline topic. Returns 0 if it can't find it.
			 *
			 * @param {Object} outline_topic Expects it to have either "topic_uuid" or "type" props.
			 * @return {number}
			 */
			const pos = (outline_topic: mergedOutlineTopic2) => {
				let position = 0;
				if ("topic_uuid" in outline_topic) {
					// @ts-ignore
					const topic = topicStore._topics[outline_topic.topic_uuid];
					if (topic)
						position = topic.position;
				} else {
					// @ts-ignore
					switch (outline_topic.type) {
						case "bookmarks":
							position = Number.MIN_SAFE_INTEGER; // Show bookmarks first
							break;
					}
				}
				return position;
			};

			outline.sort((a, b) => pos(a) - pos(b));
		}

		outline = sortAnnotations(outline);
		const mergedOutline = cleanUp(outline.map(mergeOutlineTopic));
		ensureHasAllTopics(mergedOutline);
		sortTopics(mergedOutline);
		return mergedOutline;
	}

	async _fetch(projectUuid: projectUuid, force = false) {
		await this._fetchLock.acquire("_fetch:" + projectUuid, async () => {
			if (!app.isLoggedIn)
				return;
			if (force || !this._isProjectLoaded(projectUuid))
				this._load(await app.backend.getOutline(projectUuid), projectUuid);
		});
	}

	_load(outline: outline, projectUuid: projectUuid) {
		loadObject(this._outlines, projectUuid, outline);
	}

	_loadAnnotationIntoTopic(annotationUuid: annotationUuid, topicUuid: topicUuid, projectUuid: projectUuid, prepend: boolean): number | null {
		if (!this._isProjectLoaded(projectUuid))
			return null;

		let outlineTopic = this._outlines[projectUuid]
			.find(ot => "topic_uuid" in ot && ot.topic_uuid === topicUuid);

		let position;

		if (outlineTopic === undefined) {
			outlineTopic = {topic_uuid: topicUuid, annotations: []};
			position = this._loadAnnotationIntoOutlineTopic(annotationUuid, outlineTopic, prepend);
			this._outlines[projectUuid].push(outlineTopic);
		} else
			position = this._loadAnnotationIntoOutlineTopic(annotationUuid, outlineTopic, prepend);

		return position;
	}

	_loadAnnotationIntoSpecialTopic(annotationUuid: annotationUuid, topicType: outlineTopicType, projectUuid: projectUuid, prepend: boolean): number | null {
		if (!this._isProjectLoaded(projectUuid))
			return null;

		let outlineTopic = this._outlines[projectUuid]
			.find(ot => "type" in ot && ot.type === topicType);

		let position;

		if (outlineTopic === undefined) {
			outlineTopic = {type: topicType, annotations: []};
			position = this._loadAnnotationIntoOutlineTopic(annotationUuid, outlineTopic, prepend);
			this._outlines[projectUuid].push(outlineTopic);
		} else
			position = this._loadAnnotationIntoOutlineTopic(annotationUuid, outlineTopic, prepend);

		return position;
	}

	_loadAnnotationIntoOutlineTopic(annotationUuid: annotationUuid, outlineTopic: outlineTopic, prepend: boolean): number {
		const existingOutlineItem: outlineItem | undefined = outlineTopic.annotations.find(oi => oi.annotation_uuid === annotationUuid);
		if (existingOutlineItem !== undefined)
			return existingOutlineItem.position;

		let position = 1; // for some reason this is 1-based
		if (outlineTopic.annotations.length > 0) {
			position = prepend
				? Math.min(...outlineTopic.annotations.map(a => a.position)) - 1
				: Math.max(...outlineTopic.annotations.map(a => a.position)) + 1;
		}

		const outlineItem: outlineItem = {
			annotation_uuid: annotationUuid,
			position
		};

		if (prepend)
			outlineTopic.annotations.unshift(outlineItem);
		else
			outlineTopic.annotations.push(outlineItem);

		return position;
	}

	_loadAnnotationIntoOutlineTopicUsingPosition(annotationUuid: annotationUuid, outlineTopic: outlineTopic, position: number) {
		const existingOutlineItem: outlineItem | undefined = outlineTopic.annotations.find(oi => oi.annotation_uuid === annotationUuid);
		if (existingOutlineItem !== undefined)
			return; // TODO: should we move the annotation if the position is not a match?

		const outlineItem: outlineItem = {
			annotation_uuid: annotationUuid,
			position
		};

		outlineTopic.annotations.forEach(oi => {
			if (oi.position >= position)
				oi.position++;
		});

		outlineTopic.annotations = [
			...outlineTopic.annotations.filter(oi => oi.position < position),
			outlineItem,
			...outlineTopic.annotations.filter(oi => oi.position > position)
		];
	}

	_remove(projectUuid: projectUuid) {
		delete this._outlines[projectUuid];
	}

	/**
	 * Removes the annotation from the outline, and returns the first outlineTopic it was found on, if any.
	 */
	_removeAnnotation(annotationUuid: annotationUuid, projectUuid?: projectUuid): {outlineTopic: outlineTopic; position: number} | null {
		let ret: {outlineTopic: outlineTopic; position: number} | null = null;

		Object.keys(this._outlines)
			.filter((key: projectUuid) => projectUuid === undefined || key === projectUuid)
			.forEach((key: projectUuid) =>
				this._outlines[key].forEach(ot => {
					const outlineItem = ot.annotations.find(oi => oi.annotation_uuid === annotationUuid);
					if (outlineItem) {
						if (ret === null)
							ret = {outlineTopic: ot, position: outlineItem.position};

						ot.annotations = ot.annotations
							// Remove the annotation
							.filter(oi => oi.annotation_uuid !== annotationUuid)
							// Adjust positions on annotations that are after the one we removed
							.map(oi => Object.assign({}, oi, {position: oi.position > outlineItem.position ? oi.position - 1 : oi.position}));
					}
				})
			);

		return ret;
	}

	_removeTopic(topicUuid: topicUuid, projectUuid: projectUuid) {
		if (!this._isProjectLoaded(projectUuid))
			return;

		const idx = this._outlines[projectUuid].findIndex(ot => "topic_uuid" in ot && ot.topic_uuid === topicUuid);
		if (idx !== -1)
			this._outlines[projectUuid].splice(idx, 1);
	}

	getOutlineTopicForAnnotation(annotationUuid: annotationUuid, projectUuid: projectUuid): outlineTopic | undefined {
		if (this._outlines[projectUuid] === undefined)
			return undefined;

		return this._outlines[projectUuid]
			.find(ot => ot.annotations
				.find(a => a.annotation_uuid === annotationUuid)
			);
	}

	getTopicUuidForAnnotation(annotationUuid: annotationUuid, projectUuid: projectUuid): topicUuid | undefined {
		const ot = this.getOutlineTopicForAnnotation(annotationUuid, projectUuid);
		return (ot && "topic_uuid" in ot) ? ot.topic_uuid : undefined;
	}

	_setupSyncHandlers(sync: Sync) {
		sync.setHandler(syncHandler.OUTLINE_MODIFY, this._updateFromSync.bind(this));
		sync.setHandler(syncHandler.OUTLINE_ANNOTATION_DELETE, (data: syncData[syncHandler.OUTLINE_ANNOTATION_DELETE]) => {
			annotationStore._remove(data.annotation_uuid, data.project_uuid); // this removes it from the outline as well

			// TODO (think about how to send the tab id to the content script for that special "bookmark" case)
		});
	}

	_updateFromSync(data: syncData[syncHandler.OUTLINE_MODIFY]) {
		if (this._outlines[data.project_uuid] !== undefined) // is this project's outline loaded?
			this._load(data.outline, data.project_uuid);     // load it

		if (data.orphan_annotation !== undefined)
			annotationStore._load(data.project_uuid, data.orphan_annotation);
	}

	_setupOutSyncHandlers(handler: outSyncHandler) {
		reaction(() => JSON.parse(JSON.stringify(this._outlines)), (current, previous) => {
			if (!app.isLoggedIn)
				return;

			(Object.keys(current) as projectUuid[])
				.filter(uuid => previous[uuid] !== undefined) // TODO: is this correct? should we only send a sync msg when it's both in `current` _and_ `previous`?
				.filter(uuid => !comparer.structural(current[uuid], previous[uuid])) // Only the ones that have changed
				.forEach(uuid => {
					handler(uuid, syncHandler.OUTLINE_MODIFY, {project_uuid: uuid, outline: current[uuid]});
				});
		});
	}
}

export default new OutlineStore();
